import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DURUM_RENKLERI } from '@/lib/constants';
import type { Rezervasyon } from '@/types/rezervasyon';

const DURUMLAR = [
  'Yeni Rezervasyon',
  'Onaylandı',
  'Araç Atandı',
  'Transfer Tamamlandı',
  'İptal Edildi',
  'Ödeme Bekliyor',
];

interface Props {
  rezervasyonlar: Rezervasyon[];
  siralama: { kolon: string; yon: 'asc' | 'desc' };
  durumYukleniyor: number | null;
  onSiralama: (kolon: string) => void;
  onDurumGuncelle: (id: number, durum: string) => void;
  onSilOnay: (id: number) => void;
}

function SiralaIcon({ kolon, siralama }: { kolon: string; siralama: Props['siralama'] }) {
  if (siralama.kolon !== kolon) return <i className="ri-arrow-up-down-line text-gray-600 text-xs"></i>;
  return siralama.yon === 'asc'
    ? <i className="ri-arrow-up-line text-vip-gold text-xs"></i>
    : <i className="ri-arrow-down-line text-vip-gold text-xs"></i>;
}

function DurumDropdown({
  rezervasyon,
  yukleniyor,
  onDurumGuncelle,
}: {
  rezervasyon: Rezervasyon;
  yukleniyor: boolean;
  onDurumGuncelle: (id: number, durum: string) => void;
}) {
  const [acik, setAcik] = useState(false);

  useEffect(() => {
    if (!acik) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest(`[data-status-id="${rezervasyon.id}"]`)) {
        setAcik(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [acik, rezervasyon.id]);

  if (yukleniyor) {
    return (
      <div className="flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-vip-gold text-sm"></i>
      </div>
    );
  }

  return (
    <div className="relative inline-block" data-status-id={rezervasyon.id}>
      <button
        onClick={(e) => { e.stopPropagation(); setAcik(v => !v); }}
        className={`text-[10px] px-1.5 py-0.5 rounded-md border whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity ${DURUM_RENKLERI[rezervasyon.durum] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
      >
        {rezervasyon.durum}
        <i className="ri-arrow-down-s-line ml-0.5"></i>
      </button>
      {acik && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[#1a1a1a] border border-vip-gold/20 rounded-lg shadow-xl min-w-[190px] py-1 overflow-hidden">
          {DURUMLAR.map(d => (
            <button
              key={d}
              onClick={() => { onDurumGuncelle(rezervasyon.id, d); setAcik(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 transition-colors flex items-center gap-2 ${d === rezervasyon.durum ? 'text-vip-gold' : 'text-gray-300'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${d === rezervasyon.durum ? 'bg-vip-gold' : 'bg-gray-600'}`}></span>
              {d}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RezervasyonTablosu({
  rezervasyonlar,
  siralama,
  durumYukleniyor,
  onSiralama,
  onDurumGuncelle,
  onSilOnay,
}: Props) {
  const navigate = useNavigate();

  if (rezervasyonlar.length === 0) {
    return (
      <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-10 text-center">
        <i className="ri-file-list-3-line text-4xl text-gray-700 block mb-3"></i>
        <p className="text-sm text-gray-400">Filtrelere uygun rezervasyon bulunamadı.</p>
      </div>
    );
  }

  const thCls = 'text-left py-3 px-3 text-gray-400 font-medium text-xs whitespace-nowrap';
  const thSort = `${thCls} cursor-pointer hover:text-white transition-colors`;

  return (
    <>
      {/* ── Desktop Table ── */}
      <div className="hidden md:block bg-vip-dark border border-vip-gold/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-vip-gold/10 bg-vip-black/50">
                <th className={thSort} onClick={() => onSiralama('rezervasyon_no')}>
                  <span className="flex items-center gap-1">Rez. No <SiralaIcon kolon="rezervasyon_no" siralama={siralama} /></span>
                </th>
                <th className={thSort} onClick={() => onSiralama('musteri_adi')}>
                  <span className="flex items-center gap-1">Müşteri <SiralaIcon kolon="musteri_adi" siralama={siralama} /></span>
                </th>
                <th className={thCls}>Telefon</th>
                <th className={thCls}>Transfer</th>
                <th className={thCls}>Rota</th>
                <th className={thCls}>İlçe</th>
                <th className={thSort} onClick={() => onSiralama('gelis_tarihi')}>
                  <span className="flex items-center gap-1">Tarih <SiralaIcon kolon="gelis_tarihi" siralama={siralama} /></span>
                </th>
                <th className={thCls}>Saat</th>
                <th className={thCls}>Uçak</th>
                <th className={`${thCls} text-center`}>Kişi</th>
                <th className={`${thCls} text-right`}>Ücret</th>
                <th className={thCls}>Araç</th>
                <th className={thCls}>Plaka</th>
                <th className={thCls}>Şoför</th>
                <th className={`${thCls} text-center`}>Durum</th>
                <th className={`${thCls} text-center`}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rezervasyonlar.map(r => (
                <tr key={r.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors group">
                  <td className="py-2.5 px-3 text-vip-gold font-mono whitespace-nowrap">{r.rezervasyon_no}</td>
                  <td className="py-2.5 px-3 text-white font-medium whitespace-nowrap max-w-[140px] truncate">{r.musteri_adi}</td>
                  <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap">{r.telefon}</td>
                  <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{r.transfer_tipi}</td>
                  <td className="py-2.5 px-3 text-gray-300 text-[11px] max-w-[180px] truncate">
                    {r.alinis_yeri} → {r.birak_yeri || 'Yok'}
                  </td>
                  <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap">{r.ilce}</td>
                  <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{r.gelis_tarihi}</td>
                  <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">
                    {r.gelis_saati ? String(r.gelis_saati).substring(0, 5) : '-'} {r.alinis_saati ? <span className="text-vip-gold">({String(r.alinis_saati).substring(0, 5)})</span> : ''}
                  </td>
                  <td className="py-2.5 px-3 text-gray-400">{r.ucak_kodu || '-'}</td>
                  <td className="py-2.5 px-3 text-center text-white">{r.kisi_sayisi}</td>
                  <td className="py-2.5 px-3 text-right text-vip-gold font-semibold whitespace-nowrap">
                    {Number(r.ucret).toLocaleString('tr-TR')} {r.para_birimi}
                  </td>
                  <td className="py-2.5 px-3 text-gray-400 text-[11px] whitespace-nowrap max-w-[120px] truncate">
                    {r.atanan_arac || <span className="text-gray-600 italic">Atanmadı</span>}
                  </td>
                  <td className="py-2.5 px-3 text-gray-400 text-[11px] whitespace-nowrap font-mono">
                    {r.atanan_plaka || <span className="text-gray-600 italic">-</span>}
                  </td>
                  <td className="py-2.5 px-3 text-gray-400 text-[11px] whitespace-nowrap">
                    {r.sofor || '-'}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <DurumDropdown
                      rezervasyon={r}
                      yukleniyor={durumYukleniyor === r.id}
                      onDurumGuncelle={onDurumGuncelle}
                    />
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => navigate(`/rezervasyonlar/yeni?edit=${r.id}`)}
                        title="Düzenle"
                        className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-edit-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => onSilOnay(r.id)}
                        title="Sil"
                        className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Mobile Cards ── */}
      <div className="md:hidden grid grid-cols-1 gap-3">
        {rezervasyonlar.map(r => (
          <div key={r.id} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-white">{r.musteri_adi}</p>
                <p className="text-xs text-vip-gold font-mono mt-0.5">{r.rezervasyon_no}</p>
              </div>
              <DurumDropdown
                rezervasyon={r}
                yukleniyor={durumYukleniyor === r.id}
                onDurumGuncelle={onDurumGuncelle}
              />
            </div>
            <div className="text-xs text-gray-400 space-y-1.5 mb-3">
              <p className="flex items-center gap-1">
                <i className="ri-phone-line text-gray-600"></i>
                {r.telefon}
              </p>
              <p className="flex items-center gap-1">
                <i className="ri-map-pin-line text-gray-600"></i>
                {r.alinis_yeri} → {r.birak_yeri || 'Yok'}
              </p>
              <p className="flex items-center gap-1">
                <i className="ri-calendar-line text-gray-600"></i>
                {r.gelis_tarihi} {r.gelis_saati ? String(r.gelis_saati).substring(0, 5) : ''}
                {r.alinis_saati && <span className="text-vip-gold ml-1">(Alınış: {String(r.alinis_saati).substring(0, 5)})</span>}
                {r.ucak_kodu && <span className="ml-1 text-gray-500">· {r.ucak_kodu}</span>}
              </p>
              <p className="flex items-center gap-1">
                <i className="ri-group-line text-gray-600"></i>
                {r.kisi_sayisi} Kişi
                {r.arac_tipi && <span className="ml-1 text-gray-500">· {r.arac_tipi}</span>}
              </p>
              {(r.atanan_arac || r.atanan_plaka || r.sofor) && (
                <p className="flex items-center gap-1">
                  <i className="ri-car-line text-gray-600"></i>
                  {r.atanan_arac || '-'} {r.atanan_plaka ? `· ${r.atanan_plaka}` : ''} {r.sofor ? `· ${r.sofor}` : ''}
                </p>
              )}
            </div>
            <div className="flex items-center justify-between pt-2.5 border-t border-vip-gold/10">
              <span className="text-sm font-bold text-vip-gold">
                {Number(r.ucret).toLocaleString('tr-TR')} {r.para_birimi}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => navigate(`/rezervasyonlar/yeni?edit=${r.id}`)}
                  className="w-8 h-8 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button
                  onClick={() => onSilOnay(r.id)}
                  className="w-8 h-8 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}